import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  apiGetProfileList,
  apiExportProfileList,
  GetProfileListParams,
} from "@api/profileList";
import { apiDeleteProfile } from "@api/profile";
import type { ProfileListRes } from "@/types/profile";

type ProfileListFilters = Omit<GetProfileListParams, "page">;

export function useProfileList(
  filters: ProfileListFilters,
  enabled: boolean
) {
  return useInfiniteQuery<ProfileListRes>({
    queryKey: ["profileList", filters],
    queryFn: ({ pageParam }) =>
      apiGetProfileList({
        ...filters,
        page: (pageParam as number) || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.data.length,
        0
      );
      if (totalFetched < lastPage.count) {
        return allPages.length;
      }
      return undefined;
    },
    enabled,
  });
}

export function useExportProfileList() {
  return useMutation({
    mutationFn: (filters: GetProfileListParams) =>
      apiExportProfileList(filters),
  });
}

export function useDeleteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: apiDeleteProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profileList"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
}
